import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import StatutJuridiqueTab from '../components/fiscalite/StatutJuridiqueTab';
import OverviewTab from '../components/fiscalite/OverviewTab';
import TvaTab from '../components/fiscalite/TvaTab';
import IsTab from '../components/fiscalite/IsTab';
import LocalTaxesTab from '../components/fiscalite/LocalTaxesTab';
import RegimeTab from '../components/fiscalite/RegimeTab';
import InternationalTab from '../components/fiscalite/InternationalTab';
import UrssafTab from '../components/fiscalite/UrssafTab';
import { formatMoney } from '../utils/format';
import { TAX_REGIMES } from '../data/taxes';
import { projectAnnualProfit } from '../utils/projectAnnualProfit';
import {
  Scale, Receipt, Building2, Truck as _Truck, Globe2, Settings2,
  Clock, FileText, Landmark, Users,
} from 'lucide-react';
import { useTranslation } from '../i18n';

/**
 * Fiscalité — page orchestrator. Tab navigation + summary header. The
 * heavy per-tab content lives in dedicated components under
 * `components/fiscalite/` so each piece stays under ~200 LOC and can be
 * unit-tested individually. Refactored 28/05/2026 from a 810-line monolith.
 */

const TabButton = ({ active, onClick, icon: Icon, label, badge, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
      active ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600 hover:border-ink-500'
    }`}
    data-testid={testId}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
    {badge > 0 && (
      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-danger/100 text-white text-[9px] font-bold">{badge}</span>
    )}
  </button>
);

export default function Fiscalite() {
  const { t } = useTranslation();
  const {
    taxRegime, tvaCollectedTotal, tvaDeductibleTotal, tvaPendingDeclaration,
    annualProfit, isPendingInstallment,
    cfeDue, essieuDue, month, year, phase, company,
  } = useGameStore();

  const [tab, setTab] = useState('overview');

  const regime = TAX_REGIMES[taxRegime] || TAX_REGIMES.reel_simplifie;
  const netTva = tvaCollectedTotal - tvaDeductibleTotal;

  // Pending actions count — surfaced both in the summary card and on the
  // overview tab. Kept here so the tab navigation badges and the KPI card
  // stay perfectly aligned.
  const pendingCount = (tvaPendingDeclaration ? 1 : 0) + (isPendingInstallment ? 1 : 0) +
    (cfeDue > 0 ? 1 : 0) + (essieuDue > 0 ? 1 : 0);

  // Annual IS estimate for the summary KPI card.
  // 12/02/2026 — utilise `projectAnnualProfit` (lissage début d'année).
  const { annualProfit: estimatedAnnualProfit, annualIS: estimatedIS, damped } =
    projectAnnualProfit({ annualProfit, month });
  const totalIsPaid = useGameStore.getState().isInstallmentsPaid.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-4" data-testid="fiscalite-page">
      <h2 className="text-2xl font-bold text-foreground">{t('fisc_title')}</h2>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <Receipt className="w-3.5 h-3.5" />
            <span>{t('fisc_kpi_tva_net')}</span>
          </div>
          <p className={`text-lg font-bold ${netTva >= 0 ? 'text-danger' : 'text-money'}`}>
            {netTva >= 0 ? '-' : '+'}{formatMoney(Math.abs(netTva))}
          </p>
          <p className="text-[10px] text-slate-500">{netTva >= 0 ? t('fisc_kpi_tva_to_pay') : t('fisc_kpi_tva_credit')}</p>
        </div>

        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3" data-testid="kpi-is-card">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <Landmark className="w-3.5 h-3.5" />
            <span>{t('fisc_kpi_is_estimated')}</span>
          </div>
          <p className="text-lg font-bold text-foreground">{formatMoney(estimatedIS)}</p>
          <p className="text-[10px] text-slate-500 leading-snug">
            {t('fisc_kpi_is_annual_basis')}&nbsp;: <span className="text-slate-300">{formatMoney(Math.round(estimatedAnnualProfit))}</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">{t('fisc_kpi_is_paid')}&nbsp;: {formatMoney(totalIsPaid)}</p>
          {damped && (
            <p
              className="text-[9px] text-emerald-400/80 mt-1 italic"
              data-testid="kpi-is-damped-hint"
              title={t('fisc_kpi_is_damped_tooltip')}
            >
              {t('fisc_kpi_is_damped_hint')}
            </p>
          )}
        </div>

        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <Scale className="w-3.5 h-3.5" />
            <span>{t('fisc_kpi_regime')}</span>
          </div>
          <p className="text-sm font-bold text-foreground truncate">{regime.name}</p>
          <p className="text-[10px] text-slate-500">{regime.tvaFrequency === 'monthly' ? t('fisc_kpi_tva_monthly') : t('fisc_kpi_tva_quarterly')}</p>
        </div>

        <div className={`bg-ink-800 rounded-lg border p-3 ${pendingCount > 0 ? 'border-warn/60' : 'border-ink-600'}`}>
          <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{t('fisc_kpi_deadlines')}</span>
          </div>
          <p className={`text-lg font-bold ${pendingCount > 0 ? 'text-warn' : 'text-money'}`}>
            {pendingCount > 0 ? `${pendingCount} ${t('fisc_kpi_pending_count')}` : t('fisc_kpi_none')}
          </p>
          <p className="text-[10px] text-slate-500">{t('fisc_month')} {month} - {t('fisc_year')} {year}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')} icon={FileText} label={t('fisc_tab_overview')} testId="tax-tab-overview" />
        <TabButton active={tab === 'tva'} onClick={() => setTab('tva')} icon={Receipt} label={t('fisc_tab_tva')} badge={tvaPendingDeclaration ? 1 : 0} testId="tax-tab-tva" />
        <TabButton active={tab === 'is'} onClick={() => setTab('is')} icon={Landmark} label={t('fisc_tab_is')} badge={isPendingInstallment ? 1 : 0} testId="tax-tab-is" />
        <TabButton active={tab === 'local'} onClick={() => setTab('local')} icon={Building2} label={t('fisc_tab_local')} badge={(cfeDue > 0 ? 1 : 0) + (essieuDue > 0 ? 1 : 0)} testId="tax-tab-local" />
        <TabButton active={tab === 'urssaf'} onClick={() => setTab('urssaf')} icon={Users} label={t('fisc_tab_urssaf')} testId="tax-tab-urssaf" />
        <TabButton active={tab === 'regime'} onClick={() => setTab('regime')} icon={Settings2} label={t('fisc_tab_regime')} testId="tax-tab-regime" />
        <TabButton active={tab === 'statut'} onClick={() => setTab('statut')} icon={Building2} label={t('fisc_tab_statut')} badge={company?.pendingLegalFormChange ? 1 : 0} testId="tax-tab-statut" />
        {phase >= 4 && (
          <TabButton active={tab === 'international'} onClick={() => setTab('international')} icon={Globe2} label={t('fisc_tab_intl')} testId="tax-tab-intl" />
        )}
      </div>

      {/* Tab content — each tab pulls its own slice from useGameStore */}
      {tab === 'overview' && <OverviewTab />}
      {tab === 'tva' && <TvaTab />}
      {tab === 'is' && <IsTab />}
      {tab === 'local' && <LocalTaxesTab />}
      {tab === 'urssaf' && <UrssafTab />}
      {tab === 'regime' && <RegimeTab />}
      {tab === 'statut' && (
        <StatutJuridiqueTab
          company={company}
          monthlyHistory={useGameStore.getState().monthlyHistory || []}
          onUpgrade={(targetForm) => {
            useGameStore.getState().upgradeLegalForm(targetForm);
          }}
        />
      )}
      {tab === 'international' && phase >= 4 && <InternationalTab />}
    </div>
  );
}
